import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const emptyItem = (type = "mc") => ({
  type,
  prompt: "",
  choices: type === "mc" ? ["", "", "", ""] : undefined,
  answer: "",
  pairs: type === "match" ? [{ left: "", right: "" }, { left: "", right: "" }] : undefined,
  points: 1,
});

export default function WorksheetBuilder() {
  const nav = useNavigate();
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({ title: "", subject: "math", student_id: "" });
  const [items, setItems] = useState([emptyItem("mc")]);

  useEffect(() => {
    (async () => {
      const [s, sub] = await Promise.all([api.get("/students"), api.get("/curriculum/subjects")]);
      setStudents(s.data); setSubjects(sub.data);
      if (s.data[0]) setForm((f) => ({ ...f, student_id: s.data[0].id }));
    })();
  }, []);

  const setItem = (idx, patch) => setItems((arr) => arr.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const remove = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const add = (type) => setItems((arr) => [...arr, emptyItem(type)]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.student_id) return toast.error("Pick a student");
    if (!items.length) return toast.error("Add at least one item");
    try {
      await api.post("/worksheets", { ...form, items });
      toast.success("Worksheet created");
      nav("/parent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="worksheet-builder">
      <Link to="/parent" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700"><ArrowLeft size={14} /> Back</Link>
      <div>
        <div className="chip mb-2">Worksheet builder</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Build a self-grading worksheet</h1>
        <p className="text-slate-600 mt-1">Multiple choice, fill-in-the-blank, and matching. Grades itself when your student submits.</p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <section className="p-6 rounded-3xl bg-white border border-slate-200 grid sm:grid-cols-2 gap-3">
          <input data-testid="ws-title" required placeholder="Title" value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})}
            className="sm:col-span-2 px-4 py-3 rounded-xl border border-slate-200" />
          <select data-testid="ws-student" value={form.student_id} onChange={(e)=>setForm({...form, student_id: e.target.value})}
            className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
            <option value="">Select student…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select data-testid="ws-subject" value={form.subject} onChange={(e)=>setForm({...form, subject: e.target.value})}
            className="px-4 py-3 rounded-xl border border-slate-200 bg-white">
            {subjects.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
          </select>
        </section>

        <div className="space-y-4">
          {items.map((it, idx) => (
            <div key={idx} className="p-6 rounded-3xl bg-white border border-slate-200 space-y-3" data-testid={`ws-item-${idx}`}>
              <div className="flex items-center justify-between">
                <div className="chip">{it.type === "mc" ? "Multiple choice" : it.type === "fill" ? "Fill blank" : "Matching"} · {idx+1}</div>
                <button type="button" data-testid={`ws-remove-${idx}`} onClick={()=>remove(idx)} className="text-red-500 text-sm flex items-center gap-1"><Trash2 size={14} /> Remove</button>
              </div>
              <textarea data-testid={`ws-prompt-${idx}`} required rows={2} placeholder="Question / prompt" value={it.prompt} onChange={(e)=>setItem(idx, { prompt: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200" />
              {it.type === "mc" && (
                <div className="space-y-2">
                  {(it.choices || []).map((c, ci) => (
                    <div key={ci} className="flex items-center gap-2">
                      <input type="radio" name={`ans-${idx}`} checked={String(ci) === String(it.answer)} onChange={()=>setItem(idx, { answer: String(ci) })} data-testid={`ws-mc-correct-${idx}-${ci}`} />
                      <input data-testid={`ws-mc-choice-${idx}-${ci}`} value={c} onChange={(e)=>{
                        const nc = [...it.choices]; nc[ci] = e.target.value; setItem(idx, { choices: nc });
                      }} placeholder={`Choice ${ci+1}`} className="flex-1 px-3 py-2 rounded-lg border border-slate-200" />
                    </div>
                  ))}
                  <div className="text-xs text-slate-500">Select the radio next to the correct choice.</div>
                </div>
              )}
              {it.type === "fill" && (
                <input data-testid={`ws-fill-answer-${idx}`} required value={it.answer || ""} onChange={(e)=>setItem(idx, { answer: e.target.value })}
                  placeholder="Expected answer (case-insensitive)" className="w-full px-3 py-2 rounded-lg border border-slate-200" />
              )}
              {it.type === "match" && (
                <div className="space-y-2">
                  {(it.pairs || []).map((p, pi) => (
                    <div key={pi} className="grid grid-cols-2 gap-2">
                      <input data-testid={`ws-match-left-${idx}-${pi}`} placeholder="Left" value={p.left} onChange={(e)=>{
                        const np = [...it.pairs]; np[pi] = { ...np[pi], left: e.target.value }; setItem(idx, { pairs: np });
                      }} className="px-3 py-2 rounded-lg border border-slate-200" />
                      <input data-testid={`ws-match-right-${idx}-${pi}`} placeholder="Right (correct match)" value={p.right} onChange={(e)=>{
                        const np = [...it.pairs]; np[pi] = { ...np[pi], right: e.target.value }; setItem(idx, { pairs: np });
                      }} className="px-3 py-2 rounded-lg border border-slate-200" />
                    </div>
                  ))}
                  <button type="button" onClick={()=>setItem(idx, { pairs: [...(it.pairs||[]), { left: "", right: "" }] })}
                    className="text-teal-700 text-sm font-semibold">+ Add pair</button>
                </div>
              )}
              <div className="text-xs text-slate-500">Points: <input type="number" min={1} max={10} value={it.points} onChange={(e)=>setItem(idx, { points: Number(e.target.value) })} className="w-16 px-2 py-1 rounded border border-slate-200 ml-1" /></div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="ws-add-mc" onClick={()=>add("mc")} className="px-4 py-2 rounded-full border border-slate-200 bg-white text-sm font-semibold flex items-center gap-1"><Plus size={14} /> Multiple choice</button>
          <button type="button" data-testid="ws-add-fill" onClick={()=>add("fill")} className="px-4 py-2 rounded-full border border-slate-200 bg-white text-sm font-semibold flex items-center gap-1"><Plus size={14} /> Fill blank</button>
          <button type="button" data-testid="ws-add-match" onClick={()=>add("match")} className="px-4 py-2 rounded-full border border-slate-200 bg-white text-sm font-semibold flex items-center gap-1"><Plus size={14} /> Matching</button>
        </div>

        <button data-testid="ws-submit" className="w-full py-3 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold">Save worksheet</button>
      </form>
    </div>
  );
}
